import AttachFileIcon from "@mui/icons-material/AttachFile";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SendIcon from "@mui/icons-material/Send";
import { Box, IconButton, ImageList, List, TextField, Tooltip, Typography } from "@mui/material";
import React from "react";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { Translations } from "ui/Scripts/source/models/translations";
import { NewMessageAttachmentImage } from "./NewMessageAttachmentImage";
import { NewMessageAttachmentItem } from "./NewMessageAttachmentItem";

interface NewMessageProps {
  imageAttachments?: IFileAttachment[];
  fileAttachments?: IFileAttachment[];
  message?: string;
  sendButtonTooltip?: string;
  disabled?: boolean;
  sendDisabled?: boolean;
  accept?: string;
  placeholder?: string;
  replyText?: string;
  replySender?: string;
  replyDate?: number;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onSend: () => void;
  onAddAttachment?: (attachment: FileList) => void;
  onAddAttachments?: (attachments: IFileAttachment[]) => void;
  onRemoveAttachment?: (id: string) => void;
  onRemoveReply?: () => void;
}

export const NewMessage = ({
    imageAttachments,
    fileAttachments,
    message,
    sendButtonTooltip,
    accept,
    placeholder,
    replyText,
    replySender,
    replyDate,
    disabled = false,
    sendDisabled,
    onChange,
    onSend,
    onAddAttachment,
    onRemoveAttachment,
    onRemoveReply,
}: NewMessageProps) => {
    const fileUploadRef = React.useRef<HTMLInputElement>(null);

    const handleAddAttachment = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!e.target.files || e.target.files.length < 1) return;
        onAddAttachment(e.target.files);
    };

    const handlePasteAttachments = async (e: any) => {
        if (!e.clipboardData.files || e.clipboardData.files.length < 1) return;
        onAddAttachment(e.clipboardData.files);
    };

    return (
        <Box>
            {(imageAttachments?.length > 0 || fileAttachments?.length > 0) && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "-0.5rem",
                        padding: ".5rem",
                        borderRadius: ".25rem .25rem 0 0",
                        bgcolor: "background.default",
                    }}
                >
                    {imageAttachments?.length > 0 && (
                        <ImageList cols={imageAttachments?.length === 1 ? 1 : 2} variant="quilted" sx={{ maxHeight: "40vh", overflowY: "auto" }}>
                            {imageAttachments.map((attachment) => (
                                <NewMessageAttachmentImage
                                    key={attachment.id}
                                    attachment={attachment}
                                    onClick={onRemoveAttachment}
                                />
                            ))}
                        </ImageList>
                    )}
                    {fileAttachments?.length > 0 && (
                        <List>
                            {fileAttachments.map((attachment) => (
                                <NewMessageAttachmentItem
                                    key={attachment.id}
                                    attachment={attachment}
                                    onClick={onRemoveAttachment}
                                />
                            ))}
                        </List>
                    )}
                </Box>
            )}
            <input
                ref={fileUploadRef}
                type="file"
                name="attachments"
                className="file"
                style={{ display: "none" }}
                accept={accept}
                multiple={true}
                onChange={handleAddAttachment}
            />
            {replyText &&
                <Box
                    sx={(theme) => ({
                        bgcolor: theme.palette.background.default,
                        borderLeft: ".25rem solid",
                        borderColor: "success.light",
                        borderRadius: "4px 4px 0 0",
                        padding: ".25rem .75rem",
                        marginBottom: "-2px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    })}
                >
                    <Box display="flex" flexDirection="column" sx={{ marginBottom: ".25rem" }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{replySender}</Typography>
                        <Typography>{replyText}</Typography>
                    </Box>
                    <IconButton onClick={onRemoveReply}>
                        <HighlightOffIcon />
                    </IconButton>
                </Box>
            }
            <TextField
                size="small"
                name="newMessage"
                inputProps={{ maxLength: 500 }}
                multiline
                maxRows={6}
                value={message}
                onChange={onChange}
                disabled={disabled}
                fullWidth
                placeholder={placeholder}
                onPaste={handlePasteAttachments}
                InputProps={{
                    endAdornment: (
                        <Box display="flex" alignItems="center" position="relative">
                            {!!onAddAttachment && (
                                <Tooltip title={Translations.AddAttachment}>
                                    <span>
                                        <IconButton
                                            onClick={() => fileUploadRef.current.click()}
                                            disabled={disabled}
                                        >
                                            <AttachFileIcon color="info" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title={!sendDisabled && sendButtonTooltip}>
                                <span>
                                    <IconButton
                                        onClick={onSend}
                                        color="info"
                                        disabled={sendDisabled}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    ),
                }}
            />
        </Box>
    );
};
